import { EchoEngine, type PracticeConfig, type RunKind, type RunRecord } from "./engine/engine";
import { RunDriver } from "./engine/driver";
import { dailySeed, randomSeed } from "./engine/splitmix64";
import { AudioDirector, vibrate, type InstrumentId } from "./game/audio";
import { ACHIEVEMENTS, CATALOG, rankFor, type SlotId } from "./game/catalog";
import { EchoRenderer, RenderFx } from "./game/renderer";
import { GameStore, TUTORIAL_VERSION, MAX_PRINTS } from "./game/store";
import { styleFromStore, traceprintDataURL } from "./game/traceprint";
import { seedGridHeader, seedGridRow, seedGridText } from "./game/seedgrid";
import { TutorialSim } from "./game/tutorial";
import * as S from "./ui/screens";

type Screen = "tut" | "home" | "game" | "results" | "locker" | "prints" | "shop" | "settings" | "practice";

const PRICE = "$9.99"; // web twin placeholder; iOS always uses StoreKit displayPrice
// The browser build is a public demo: anything purchasable routes to the App Store.
const APP_STORE_URL = "https://apps.apple.com/app/id6799600497";

export class App {
  private store = new GameStore();
  private audio = new AudioDirector();
  private renderer: EchoRenderer;
  private fx = new RenderFx();

  private screen: Screen;
  private engine: EchoEngine | null = null;
  private driver: RunDriver | null = null;
  private tut: TutorialSim | null = null;
  private readyT = 0;
  private endLinger = 0;
  private paused = false;
  private resuming = false;
  private resumeNum = 3;
  private resumeTimer: number | null = null;

  private lockerTab: SlotId = "core";
  private previewNote = "YOUR CURRENT TIMELINE";
  private printSel: string | null = null;
  private sheetOpen = false;
  private beat4 = false;
  private practiceCfg: PracticeConfig & { grid: boolean } = {
    A: true, B: false, C: false, D: false, collision: true, infinite: false, grid: true,
  };

  private results: S.ResultsView | null = null;
  private lastRecord: RunRecord | null = null;
  private toastTimer: number | null = null;
  private lastTs = 0;
  private keys = new Set<string>();

  constructor(
    private readonly stage: HTMLElement,
    private readonly canvas: HTMLCanvasElement,
    private readonly ui: HTMLElement,
    private readonly toasts: HTMLElement,
  ) {
    this.renderer = new EchoRenderer(canvas, this.store);
    this.screen = this.store.data.tutorialVersion >= TUTORIAL_VERSION ? "home" : "tut";
    this.syncAudioPrefs();
    this.bindInput();
    this.renderUI();
    if (this.screen === "tut") this.startTutorial();
    // ?headless=1 drives the loop on a timer for automated testing where
    // requestAnimationFrame is starved (hidden/uncomposited tabs).
    if (new URLSearchParams(location.search).get("headless") === "1") {
      (window as unknown as { __app: App }).__app = this; // test hook, headless only
      setInterval(() => this.frame(performance.now()), 16);
    } else {
      const raf = (ts: number): void => {
        requestAnimationFrame(raf);
        this.frame(ts);
      };
      requestAnimationFrame(raf);
    }
  }

  // ---------- helpers ----------

  private syncAudioPrefs(): void {
    const s = this.store.data.settings;
    this.audio.fxEnabled = s.fx;
    this.audio.musicEnabled = s.music;
    this.audio.instrument = this.store.data.equipped.instrument as InstrumentId;
  }

  private toast(msg: string): void {
    this.toasts.innerHTML = `<div class="toast-wrap"><div class="toast">${msg}</div></div>`;
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => (this.toasts.innerHTML = ""), 1800);
  }

  private haptic(pattern: number | number[]): void {
    if (this.store.data.settings.haptics) vibrate(pattern);
  }

  private todayKey(): string {
    return new Date().toISOString().slice(0, 10);
  }

  // ---------- run lifecycle ----------

  private newRun(kind: RunKind): void {
    const training = kind === "classic" && this.store.trainingActive;
    let seed: bigint;
    if (kind === "daily") {
      const now = new Date();
      seed = dailySeed(now.getUTCFullYear(), now.getUTCMonth() + 1, now.getUTCDate());
    } else {
      seed = randomSeed();
    }
    this.engine = new EchoEngine({
      kind,
      seed,
      training,
      practice: kind === "practice" ? this.practiceCfg : undefined,
    });
    this.driver = new RunDriver(this.engine);
    this.fx.reset();
    this.readyT = 0.7;
    this.endLinger = 0;
    this.paused = false;
    this.resuming = false;
    this.results = null;
    this.lastRecord = null;
    this.audio.ensure();
    this.audio.runStart();
    if (this.store.data.settings.music) this.audio.startMusic();
    this.screen = "game";
    this.renderUI();
  }

  private startTutorial(): void {
    this.tut = new TutorialSim();
    this.beat4 = false;
    this.screen = "tut";
    this.renderUI();
  }

  private finishRun(): void {
    const engine = this.engine;
    if (!engine) return;
    this.audio.stopMusic();
    const rec = engine.buildRunRecord();
    this.lastRecord = rec;

    if (engine.kind === "practice") {
      this.toast(engine.win ? "Lab timeline survived" : "Lab run over — no score in Practice");
      this.screen = "practice";
      this.engine = null;
      this.driver = null;
      this.renderUI();
      return;
    }

    const prevBest = engine.kind === "daily" ? (this.store.data.bestDaily[this.todayKey()] ?? 0) : this.store.data.bestClassic;
    const applied = this.store.applyRun(rec, engine.edgeSteps, engine.echoAliveSteps);
    const print = traceprintDataURL(rec, styleFromStore(this.store), 640);

    // auto-save first completed print and every new personal best (spec §10.3)
    let saved = false;
    if (!rec.wasTraining && (this.store.data.prints.length === 0 || applied.isBest)) {
      const saveRec = this.store.savePrint({
        url: print,
        score: rec.score,
        dateISO: rec.dateISO,
        fav: false,
        win: rec.outcome === "cleared",
        combo: rec.longestCombo,
        durationTenths: Math.round(rec.duration * 10),
        kind: rec.kind,
      });
      saved = saveRec !== null;
    }

    let rankUp: string | null = null;
    if (engine.kind === "classic" && !rec.wasTraining && applied.isBest) {
      const before = rankFor(prevBest).name;
      const after = rankFor(rec.score).name;
      if (before !== after) rankUp = after;
    }

    const unlockCards = applied.newAchievements
      .slice(0, 2)
      .map((id) => ({
        name: this.store.achievementRewardName(id) ?? (ACHIEVEMENTS[id]?.name ?? id.toUpperCase()),
        desc: ACHIEVEMENTS[id]?.desc ?? "",
      }));

    const lastHit = rec.collisions[rec.collisions.length - 1];
    this.results = {
      win: rec.outcome === "cleared",
      score: rec.score,
      shardPts: rec.shardScore,
      bonus: rec.completionBonus,
      combo: rec.longestCombo,
      time: rec.duration,
      attr: rec.outcome === "cleared" || !lastHit ? null : `YOUR -${lastHit.delay}S ECHO CAUGHT YOU`,
      isBest: applied.isBest && rec.score > 0,
      training: rec.wasTraining,
      daily: rec.kind === "daily",
      print,
      rankUp,
      unlockCards,
      saved,
      archiveFull: this.store.data.prints.length >= MAX_PRINTS,
      grid: rec.wasTraining || rec.kind === "practice" ? null : seedGridRow(rec),
      copied: false,
    };
    this.screen = "results";
    this.renderUI();
  }

  // ---------- input ----------

  private bindInput(): void {
    const toArena = (ev: PointerEvent): { x: number; y: number } | null => {
      const L = this.renderer.layout();
      if (!L) return null;
      const rect = this.canvas.getBoundingClientRect();
      return {
        x: (ev.clientX - rect.left - L.ax) / L.s,
        y: (ev.clientY - rect.top - L.ay) / L.s,
      };
    };
    const setTarget = (ev: PointerEvent): void => {
      const p = toArena(ev);
      if (!p) return;
      const offset = this.store.data.settings.offset;
      if (this.engine) this.engine.setInput(p.x, p.y - offset, true);
      if (this.tut) this.tut.setInput(p.x, p.y - offset, true);
    };
    this.stage.addEventListener("pointerdown", (ev) => {
      this.audio.ensure();
      const target = ev.target as HTMLElement;
      if (target.closest("[data-action]")) return; // UI buttons handle themselves
      const inRun = this.screen === "game" && this.engine && !this.engine.over && !this.paused;
      const inTut = this.screen === "tut" && this.tut && !this.beat4;
      if (inRun || inTut) {
        setTarget(ev);
        this.audio.touchDown();
        try {
          this.stage.setPointerCapture(ev.pointerId);
        } catch {
          /* unsupported */
        }
      }
    });
    this.stage.addEventListener("pointermove", (ev) => {
      if (this.engine?.touching || this.tut?.touching) setTarget(ev);
    });
    const release = (): void => {
      this.engine?.release();
      if (this.tut) this.tut.touching = false;
    };
    this.stage.addEventListener("pointerup", release);
    this.stage.addEventListener("pointercancel", release);

    // debug keyboard (spec §4.2: 205 units/s) — desktop playtesting
    window.addEventListener("keydown", (ev) => {
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "w", "a", "s", "d"].includes(ev.key)) {
        this.keys.add(ev.key);
        ev.preventDefault();
      }
    });
    window.addEventListener("keyup", (ev) => this.keys.delete(ev.key));

    document.addEventListener("visibilitychange", () => {
      if (
        document.hidden &&
        this.screen === "game" &&
        this.engine &&
        !this.engine.over &&
        !this.paused
      ) {
        this.pause();
      }
    });

    this.ui.addEventListener("click", (ev) => {
      const el = (ev.target as HTMLElement).closest<HTMLElement>("[data-action]");
      if (!el) return;
      this.audio.ensure();
      this.handleAction(el.dataset.action ?? "", el.dataset);
    });
  }

  private keyboardDir(): { x: number; y: number } | null {
    let x = 0;
    let y = 0;
    if (this.keys.has("ArrowLeft") || this.keys.has("a")) x -= 1;
    if (this.keys.has("ArrowRight") || this.keys.has("d")) x += 1;
    if (this.keys.has("ArrowUp") || this.keys.has("w")) y -= 1;
    if (this.keys.has("ArrowDown") || this.keys.has("s")) y += 1;
    if (x === 0 && y === 0) return null;
    const n = Math.sqrt(x * x + y * y);
    return { x: x / n, y: y / n };
  }

  // ---------- actions ----------

  private handleAction(action: string, data: DOMStringMap): void {
    switch (action) {
      case "play":
        this.newRun("classic");
        break;
      case "daily":
        this.newRun("daily");
        break;
      case "again":
        this.newRun(this.lastRecord?.kind === "daily" ? "daily" : "classic");
        break;
      case "homeBtn":
        this.cancelResumeTimer();
        this.audio.stopMusic();
        this.engine = null;
        this.driver = null;
        this.printSel = null;
        this.screen = "home";
        this.renderUI();
        break;
      case "practice":
        this.screen = "practice";
        this.renderUI();
        break;
      case "locker":
        this.screen = "locker";
        this.previewNote = "YOUR CURRENT TIMELINE";
        this.renderUI();
        break;
      case "prints":
        this.screen = "prints";
        this.renderUI();
        break;
      case "shop":
        this.screen = "shop";
        this.renderUI();
        break;
      case "settings":
        this.screen = "settings";
        this.renderUI();
        break;
      case "pause":
        this.pause();
        break;
      case "resume":
        this.beginResume();
        break;
      case "restart": {
        this.cancelResumeTimer();
        const kind = this.engine?.kind ?? "classic";
        this.newRun(kind);
        break;
      }
      case "quit":
        this.cancelResumeTimer();
        this.audio.stopMusic();
        this.engine = null;
        this.driver = null;
        this.paused = false;
        this.screen = "home";
        this.renderUI();
        break;
      case "skipTut":
        this.completeTutorial(false);
        break;
      case "startTraining":
        this.completeTutorial(true);
        break;
      case "replayTut":
        this.startTutorial();
        break;
      case "copyGrid": {
        if (!this.results || !this.lastRecord) break;
        const text = seedGridText(this.lastRecord);
        void navigator.clipboard?.writeText(text).then(
          () => { this.results!.copied = true; this.toast("Seed grid copied"); this.renderUI(); },
          () => this.toast(text),
        );
        break;
      }
      case "shareTrace": {
        if (!this.results || !this.lastRecord) break;
        void (async () => {
          try {
            const blob = await (await fetch(this.results!.print)).blob();
            const file = new File([blob], "echo-dodge-traceprint.jpg", { type: "image/jpeg" });
            const caption = `${seedGridHeader(this.lastRecord!)}\n${seedGridRow(this.lastRecord!)}\nCan you beat it? https://apps.apple.com/app/id6799600497`;
            if (navigator.canShare?.({ files: [file] })) {
              await navigator.share({ files: [file], text: caption });
            } else {
              const a = document.createElement("a");
              a.href = this.results!.print;
              a.download = "echo-dodge-traceprint.jpg";
              a.click();
              this.toast("Traceprint exported");
            }
          } catch {
            /* user dismissed share sheet */
          }
        })();
        break;
      }
      case "saveTrace": {
        if (!this.results || this.results.saved || !this.lastRecord) break;
        if (this.store.data.prints.length >= MAX_PRINTS) {
          this.toast("Archive full — remove a Traceprint first");
          break;
        }
        const rec = this.lastRecord;
        this.store.savePrint({
          url: this.results.print,
          score: rec.score,
          dateISO: rec.dateISO,
          fav: false,
          win: rec.outcome === "cleared",
          combo: rec.longestCombo,
          durationTenths: Math.round(rec.duration * 10),
          kind: rec.kind,
        });
        this.results.saved = true;
        this.toast("Traceprint saved to archive");
        this.renderUI();
        break;
      }
      case "lockerTab":
        this.lockerTab = (data.tab as SlotId) ?? "core";
        this.previewNote = "YOUR CURRENT TIMELINE";
        this.renderUI();
        break;
      case "lockerItem": {
        const slot = data.slot as SlotId;
        const id = data.id ?? "";
        if (this.store.owns(slot, id)) {
          this.store.equip(slot, id);
          this.syncAudioPrefs();
          this.renderer.invalidateBackground();
          this.audio.pickup(1);
          this.renderUI();
        } else {
          const item = CATALOG[slot]?.find((i) => i.id === id);
          if (item?.prism) {
            this.previewNote = `PREVIEW: ${item.name} · PRISM TIMELINE PACK · ${PRICE} — TAP SHOP TO BUY`;
            this.renderUI();
          } else if (item?.ach) {
            this.toast(`Locked — ${ACHIEVEMENTS[item.ach]?.desc ?? ""}`);
          }
        }
        break;
      }
      case "openPrint":
        this.printSel = data.id ?? null;
        this.renderUI();
        break;
      case "closePrint":
        this.printSel = null;
        this.renderUI();
        break;
      case "favPrint":
        if (data.id) this.store.toggleFav(data.id);
        this.renderUI();
        break;
      case "sharePrint": {
        const p = this.store.data.prints.find((x) => x.id === data.id);
        if (p) {
          const a = document.createElement("a");
          a.href = p.url;
          a.download = `echo-dodge-traceprint-${p.runNumber}.jpg`;
          a.click();
          this.toast("Traceprint exported");
        }
        break;
      }
      case "deletePrint": {
        const p = this.store.data.prints.find((x) => x.id === data.id);
        if (p && confirm("Delete this Traceprint? This cannot be undone.")) {
          this.store.deletePrint(p.id);
          this.printSel = null;
          this.renderUI();
        }
        break;
      }
      case "buy":
      case "getApp":
        window.open(APP_STORE_URL, "_blank", "noopener");
        break;
      case "cancelBuy":
        this.sheetOpen = false;
        this.renderUI();
        break;
      case "restore":
        this.toast(this.store.data.prism ? "Purchases restored" : "Nothing to restore");
        break;
      case "privacy":
        this.toast("Echo Dodge stores everything on this device. Nothing is collected.");
        break;
      case "support":
        this.toast("support@echododge.example — replace before shipping");
        break;
      case "toggleSetting": {
        const key = data.key as keyof typeof this.store.data.settings;
        const s = this.store.data.settings;
        if (key && key in s && typeof s[key] === "boolean") {
          (s[key] as boolean) = !s[key];
          this.store.save();
          this.syncAudioPrefs();
          this.renderer.invalidateBackground();
          this.renderUI();
        }
        break;
      }
      case "setOffset":
        this.store.data.settings.offset = Number(data.v) as 44 | 24 | 0;
        this.store.save();
        this.renderUI();
        break;
      case "practiceEcho": {
        const id = data.id as "A" | "B" | "C" | "D";
        this.practiceCfg[id] = !this.practiceCfg[id];
        this.renderUI();
        break;
      }
      case "practiceRule": {
        const key = data.key as "collision" | "infinite" | "grid";
        this.practiceCfg[key] = !this.practiceCfg[key];
        this.renderUI();
        break;
      }
      case "startPractice":
        if (!this.practiceCfg.A && !this.practiceCfg.B && !this.practiceCfg.C && !this.practiceCfg.D) {
          this.toast("Enable at least one echo to study");
          break;
        }
        this.newRun("practice");
        break;
      default:
        break;
    }
  }

  private cancelResumeTimer(): void {
    if (this.resumeTimer !== null) {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = null;
    }
    this.resuming = false;
  }

  private completeTutorial(startRun: boolean): void {
    this.store.data.tutorialVersion = TUTORIAL_VERSION;
    this.store.save();
    this.tut = null;
    this.beat4 = false;
    if (startRun) this.newRun("classic");
    else {
      this.screen = "home";
      this.renderUI();
    }
  }

  private pause(): void {
    if (this.screen !== "game" || !this.engine || this.engine.over) return;
    this.paused = true;
    this.resuming = false;
    this.engine.release();
    this.renderUI();
  }

  private beginResume(): void {
    this.resuming = true;
    this.resumeNum = 3;
    this.renderUI();
    const tick = (): void => {
      this.resumeNum--;
      if (this.resumeNum <= 0) {
        this.paused = false;
        this.resuming = false;
        this.engine?.release(); // clear active touch state on resume (spec §9.2)
        this.lastTs = 0;
        this.renderUI();
      } else {
        this.renderUI();
        this.resumeTimer = window.setTimeout(tick, 700);
      }
    };
    this.resumeTimer = window.setTimeout(tick, 700);
  }

  // ---------- frame loop ----------

  private frame(ts: number): void {
    const dt = Math.min((ts - (this.lastTs || ts)) / 1000, 0.1);
    this.lastTs = ts;
    this.renderer.ambientT += dt;

    if (this.screen === "game" && this.engine && this.driver) {
      const engine = this.engine;
      if (!this.paused) {
        if (this.readyT > 0) {
          this.readyT -= dt;
        } else if (!engine.over) {
          const kd = this.keyboardDir();
          engine.setKeyDirection(kd?.x ?? 0, kd?.y ?? 0);
          const events = this.driver.update(dt);
          this.fx.applyEvents(events, engine, this.store.data.settings.reduced);
          for (const e of events) {
            switch (e.type) {
              case "pickup":
                this.audio.pickup(e.combo);
                this.haptic(e.combo >= 4 ? 18 : 10);
                break;
              case "comboTimeout":
                this.audio.comboTimeout();
                break;
              case "warnTick":
                this.audio.warnTick();
                break;
              case "unlock":
                this.audio.unlock(e.echoId);
                this.audio.addStem(e.echoId);
                this.haptic(8);
                break;
              case "hit":
                this.audio.hit();
                this.haptic([30, 60, 14]);
                break;
              case "secondPulse":
                this.audio.secondPulse();
                break;
              case "ended":
                if (e.win) {
                  this.audio.clear(engine.defs.map((d) => d.id));
                  this.haptic([12, 40, 12, 40, 24]);
                } else {
                  this.audio.gameOver();
                  this.haptic(60);
                }
                this.endLinger = e.win ? 0.55 : 0.7;
                break;
            }
          }
        } else if (this.endLinger > 0) {
          this.endLinger -= dt;
          if (this.endLinger <= 0) this.finishRun();
        }
        this.fx.tick(dt, engine.touching);
      }
      this.renderer.drawRun(engine, this.fx, this.readyT, engine.training);
      const L = this.renderer.layout();
      if (L) this.renderer.drawHUD(engine, L, this.fx);
    } else if (this.screen === "tut" && this.tut) {
      if (!this.beat4) {
        this.tut.update(dt);
        for (const e of this.tut.events.splice(0)) {
          switch (e) {
            case "pickup":
              this.audio.pickup(1);
              this.haptic(10);
              break;
            case "beat2":
              this.renderTutCopy();
              break;
            case "beat3":
              this.audio.unlock("A");
              this.renderTutCopy();
              break;
            case "beat4":
              this.beat4 = true;
              this.renderUI();
              break;
            case "hit":
              this.audio.hit();
              this.haptic(30);
              break;
          }
        }
      }
      this.drawTutorial();
    } else {
      const L = this.renderer.layout();
      if (L) {
        const ctx = this.canvas.getContext("2d")!;
        this.renderer.drawAmbient(ctx, L);
      }
    }
  }

  private renderTutCopy(): void {
    if (this.screen === "tut" && this.tut && !this.beat4) {
      this.ui.innerHTML = S.tutorialOverlay(this.tut.copy());
    }
  }

  private drawTutorial(): void {
    const L = this.renderer.layout();
    if (!L || !this.tut) return;
    const ctx = this.canvas.getContext("2d")!;
    const tut = this.tut;
    this.renderer.clearCanvas();

    // recorded route (dashed) during beats 2-3
    if (tut.beat >= 2 && tut.route.length > 1) {
      ctx.save();
      ctx.beginPath();
      for (let i = 0; i < tut.route.length; i += 2) {
        const p = this.renderer.A(L, tut.route[i].x, tut.route[i].y);
        if (i === 0) ctx.moveTo(p[0], p[1]);
        else ctx.lineTo(p[0], p[1]);
      }
      ctx.strokeStyle = "rgba(255,97,210,0.35)";
      ctx.setLineDash([4, 5]);
      ctx.lineWidth = 1.4 * L.s;
      ctx.stroke();
      ctx.restore();
      const ep = tut.echoPos();
      if (ep) {
        const pending = tut.beat === 3 && !tut.collidable;
        this.renderer.drawEchoHead(
          ctx, L, ep.x, ep.y, "#FF61D2", "circle", 1, "-2.4s",
          pending ? 0.45 : 1,
          this.store.data.settings.reduced,
        );
      }
    }

    // shard
    if (tut.shard) {
      const P = this.renderer.A(L, tut.shard.x, tut.shard.y);
      ctx.save();
      ctx.translate(P[0], P[1]);
      ctx.rotate(tut.t * 1.8);
      ctx.fillStyle = "#FFE37B";
      ctx.beginPath();
      for (let i = 0; i < 8; i++) {
        const r = i % 2 === 0 ? 12 * L.s : 4.2 * L.s;
        const a = (i * Math.PI) / 4;
        if (i === 0) ctx.moveTo(Math.cos(a) * r, Math.sin(a) * r);
        else ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    // player
    const P = this.renderer.A(L, tut.px, tut.py);
    ctx.save();
    ctx.strokeStyle = "#42DAF6";
    ctx.lineWidth = 2.2 * L.s;
    ctx.beginPath();
    ctx.arc(P[0], P[1], 10 * L.s, 0, 6.283);
    ctx.stroke();
    ctx.fillStyle = "#B9F8FF";
    ctx.beginPath();
    ctx.arc(P[0], P[1], 5.5 * L.s, 0, 6.283);
    ctx.fill();
    ctx.restore();
  }

  // ---------- UI rendering ----------

  private renderUI(): void {
    let html = "";
    switch (this.screen) {
      case "home": {
        const today = this.todayKey();
        const dailyBest = this.store.data.bestDaily[today] ?? 0;
        html = S.homeScreen(this.store, dailyBest > 0, dailyBest);
        break;
      }
      case "game":
        html = this.paused ? S.runHUDOverlay() + S.pauseOverlay(this.resuming, this.resumeNum) : S.runHUDOverlay();
        break;
      case "results":
        if (this.results) html = S.resultsScreen(this.results);
        break;
      case "tut":
        html = this.beat4 ? S.goalCard() : S.tutorialOverlay(this.tut?.copy() ?? "");
        break;
      case "locker":
        html = S.lockerScreen(this.store, this.lockerTab, this.previewNote);
        break;
      case "prints":
        html = S.printsScreen(this.store) + (this.printSel ? S.printDetail(this.store, this.printSel) : "");
        break;
      case "shop":
        html = S.shopScreen(this.store, PRICE) + (this.sheetOpen ? S.shopSheet(PRICE) : "");
        break;
      case "settings":
        html = S.settingsScreen(this.store);
        break;
      case "practice":
        html = S.practiceScreen(this.practiceCfg, this.store.equippedItem("palette").cols);
        break;
    }
    this.ui.innerHTML = html;
  }
}
